// Modal component to edit a task. Autosaving: edits persist as they're made
// (there is no Save button), so the footer's only action is "Done", which
// simply closes the modal. Closing mid-debounce is safe -- EditForm flushes
// pending edits on unmount and the global SaveStatusPill reports the result.
//
// Usage:
//  const saveEditedItem = useCallback(async (newItem) => {
//    ...   // called per autosave flush; must NOT close this modal
//  }, [item]);
//   import ModalEditForm from './ModalEditForm';
//   import { taskFields } from '../utilities/TaskFields.js';
//   const [showEditTask, setShowEditTask] = React.useState(false);
//   <ModalEditForm
//     show={showEditTask}
//     onHide={() => setShowEditTask(false)}
//     fields={taskFields}    <-- or ItemFields = [{
//                                     name: "Item Title",
//                                     key: "{Key into editItem[]}"
//                                     type="[text|date|number|select]"
//                                     selects=[key:[<selectItems>]]
//                                    }]
//     item={editItem}
//     save={saveEditedItem}
//     delete={deleteItem}  // If you want a delete button, not specified for none.
//     title= optional title
//     deriveState= optional (previous, next) => state, to keep dependent fields in sync.
//     validate / buildPayload / recordKeyPrefix / autosave -- passed through
//         to EditForm (see EditForm.jsx for the autosave contract). Pass
//         `autosave` when the page behind this modal edits the same record
//         through its own controls and must share one edit session.
//   />
// "selects" is only required if there is a taskFields of type "select".
// It is a map of key for the field mapping to an array of items for the
// select.
//


import { useRef } from 'react';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import { EditForm } from './EditForm';

function ModalEditForm(props) {
  // Ref to the first text/number input in EditForm. We focus it on
  // `onEntered` (i.e. after the modal animation finishes) so the mobile
  // keyboard, primed by the Add click handler via `primeKeyboard()`, hands
  // off to the real field instead of closing. Bootstrap's own focus trap is
  // disabled below so it doesn't yank focus off the primer before we get
  // here.
  const firstInputRef = useRef(null);

  const handleEntered = () => {
    const node = firstInputRef.current;
    if (!node) return;
    // The ref points at either a plain DOM <input> (text / number fields)
    // or a react-datepicker instance (date fields, e.g. logbook entries
    // where the first field is `date`). DatePicker exposes `setFocus()`,
    // which focuses its underlying input -- that's what hands the mobile
    // keyboard off from the primer to the real field. Fall back to
    // `.focus()` for plain inputs.
    if (typeof node.setFocus === 'function') {
      node.setFocus();
    } else if (typeof node.focus === 'function') {
      node.focus();
    }
  };

  return (
    <Modal
      show={props.show}
      onHide={props.onHide}
      autoFocus={false}
      enforceFocus={false}
      onEntered={handleEntered}
    >
      <Modal.Header closeButton>
        <Modal.Title>{props.title || 'Edit Item'}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <EditForm
          item={props.item}
          fields={props.fields}
          saveItem={props.save}
          deleteItem={props.delete}
          firstInputRef={firstInputRef}
          deriveState={props.deriveState}
          validate={props.validate}
          buildPayload={props.buildPayload}
          recordKeyPrefix={props.recordKeyPrefix}
          autosave={props.autosave}
        />
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={props.onHide}>
          Done
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default ModalEditForm;
